<script setup lang="ts">
import {
    faArrowLeft,
    faMap,
    faMapMarkerAlt,
    faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { useData } from '../../_composables/useData';
import StarRating from '@components/util/StarRating.vue';
import HotelPoints from '@components/util/hotel-card/HotelPoints.vue';
import CardLabels from '@components/util/hotel-card/CardLabels.vue';

const emit = defineEmits<{
    showMap: [];
}>();

const hotelModule = useHotelModule();
const { hotelPackage, title, loadingClass } = useData();
const layoutStore = useLayoutStore();
const { inProcessFloatings } = classToRefs(layoutStore);

const scrollTo = () => {
    const element = document.getElementById('comments');
    if (!element) {
        return;
    }
    const top = element.getBoundingClientRect().top + window.scrollY - 48;
    window.scrollTo({ top, behavior: 'smooth' });
};
const loadingRoom = computed(
    () =>
        hotelModule.isLoadingHotelInfo ||
        hotelPackage.value.isLoadingRooms !== false
);
const rooms = computed(() => minRoom.value?.rooms || 0);
const isOneSelectableRoomSelected = computed(
    () =>
        hotelPackage.value.selectedPricesWithPriceClass?.[0]?.[1]
            ?.onlyThisTypeSelectable
);
const prices = computed(() => {
    if (isOneSelectableRoomSelected.value) {
        return hotelPackage.value.prices.filter(
            p => p.id === hotelPackage.value.selectedPrices![0].id
        );
    } else {
        return hotelPackage.value.prices;
    }
});
const minRoom = computed(() => {
    if (!prices.value?.length) {
        return undefined;
    }
    return prices.value
        .filter(price => price.cheapestPriceClass)
        .map(price => ({
            price: price.cheapestPriceClass!.singlePayable,
            rooms: price.capacity,
        }))
        .reduce(
            (prev, current) => {
                if (prev === undefined) {
                    return current;
                }
                if (prev.price < current.price) {
                    return prev;
                }
                return current;
            },
            undefined as
                | {
                      price: number;
                      rooms: number;
                  }
                | undefined
        );
});
const payable = computed(() => minRoom.value?.price || 0);
const payablePrefix = computed(
    () => `${hotelPackage.value.nights} شب اتاق ${rooms.value} تخته، قیمت از`
);
onMounted(() => layoutStore.getFloatingsInProcess());
</script>
<template>
    <div class="details-content">
        <div class="ratings">
            <StarRating
                v-show="hotelPackage.percentStar"
                :number-of-stars="5"
                :percent="hotelPackage.percentStar"
                size="medium"
            />
            <HotelPoints
                v-show="hotelPackage.points10"
                :value="hotelPackage.points10 || 0"
                size="small"
                @click="scrollTo()"
            />
        </div>
        <mr-typography size="x-large" weight="700" :class="loadingClass">
            {{ title }}
        </mr-typography>
        <template v-if="hotelPackage.serverLabels?.length">
            <CardLabels :labels="hotelPackage.serverLabels" />
            <mr-divider thickness="1px" />
        </template>
        <mr-price-re
            v-show="payable || loadingRoom"
            :class="{ 'loading-skeleton': loadingRoom }"
            type="medium"
            :price="payable"
            :prefix="payablePrefix"
        />
        <mr-divider thickness="1px" />
        <template v-if="inProcessFloatings.length">
            <NuxtLink
                class="floating-warning"
                :to="`/reserve/floating?billCode=${inProcessFloatings[0].billCode}&mac=${inProcessFloatings[0].mac}`"
            >
                <mr-icon :icon="faSpinner" color="var(--blue-500)" pulse />
                <mr-typography weight="500" color="var(--blue-500)" grow>
                    شما {{ inProcessFloatings.length }} شناوری فعال دارید.
                </mr-typography>
                <mr-icon :icon="faArrowLeft" color="var(--blue-500)" />
            </NuxtLink>
            <mr-divider thickness="1px" />
        </template>
        <div class="address-container">
            <mr-icon
                size="small"
                :icon="faMapMarkerAlt"
                color="var(--cool-gray-600)"
            />
            <mr-typography
                size="large"
                weight="500"
                color="var(--cool-gray-600)"
            >
                {{ hotelPackage.address }}
            </mr-typography>
        </div>
        <mr-button
            variant="bordered"
            :right-icon="faMap"
            @click="emit('showMap')"
        >
            مشاهده روی نقشه
        </mr-button>
    </div>
</template>
<style scoped lang="scss">
.details-content {
    .ratings {
        display: flex;
        align-items: center;
        gap: 12px;
        justify-content: space-between;
    }
    .floating-warning {
        display: flex;
        align-items: center;
        gap: 4px;
    }
    .address-container {
        display: flex;
        align-items: center;
        gap: 8px;
    }
}
</style>
